import { AbstractClient, CompositionClient } from "@/app/client";
import * as types from "@/app/types";
import { UUID } from "crypto";
import APIMembersSub from "./members/client";

export default class APICompanies extends CompositionClient {
  constructor(client: AbstractClient) {
    super(client);
  }

  members = new APIMembersSub(this._client);

  getMe = async (): Promise<types.CompanyResponse> => {
    let res = await this._fetch({
      url: `/companies/me`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  getMembers = async (): Promise<types.UserResponse[]> => {
    let res = await this._fetch({
      url: `/companies/members`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Server-paginated, searchable roster slice for the Users tab. Returns the
  // page plus the total match count so the client never loads every account.
  searchMembers = async (
    query: types.CompanyMembersSearchParams,
  ): Promise<types.CompanyMembersPage> => {
    let res = await this._fetch({
      url: `/companies/members/search`,
      method: "GET",
      params: query,
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  updateMe = async ({
    ...body
  }: types.CompanyUpdate): Promise<types.CompanyResponse> => {
    let res = await this._fetch({
      url: `/companies/`,
      method: "PUT",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // ---- Owner-only user administration (Company Management dashboard) ----

  createUser = async (
    body: types.CompanyUserCreate,
  ): Promise<types.UserResponse> => {
    let res = await this._fetch({
      url: `/companies/users`,
      method: "POST",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  updateUser = async (
    userId: UUID,
    body: types.CompanyUserUpdate,
  ): Promise<types.UserResponse> => {
    let res = await this._fetch({
      url: `/companies/users/${userId}`,
      method: "PATCH",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  resetUserPassword = async (
    userId: UUID,
    body: { password?: string | null },
  ): Promise<types.ResetPasswordResponse> => {
    let res = await this._fetch({
      url: `/companies/users/${userId}/reset-password`,
      method: "POST",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  sendUserRecovery = async (userId: UUID): Promise<void> => {
    await this._fetch({
      url: `/companies/users/${userId}/send-recovery`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
    });
  };
}
